import { useContext } from 'react';
import styled from 'styled-components';
import { AccountInfoContext } from '../account/account-info';
import bgImg from '../../../../assets/images/static_pages/tcr_banner_bg.svg';
import dotImage from '../../../../assets/images/static_pages/tcr_banner_dots.svg';
import { X } from 'react-feather';
import { useNavigationActions } from '@/lib/navigation';

export type BannerStep =
  | 'trial-register'
  | 'trial-pending'
  | 'trial-upgrade'
  | 'paid-register'
  | 'paid-choose-number'
  | 'paid-sync'
  | 'toll-free';

interface ReminderHeaderProps {
  bannerStep: BannerStep;
  handleDisableBanner?: () => void;
}

const BANNER_CONFIG: Record<
  BannerStep,
  {
    title: string;
    description: string;
    ctaLabel: string;
    learnMoreUrl?: string;
  }
> = {
  'trial-register': {
    title: 'Register for A2P for Free',
    description:
      'Submit your business info to get approved for SMS texting. Avochato waives the application fees.',
    ctaLabel: 'Register Now',
    learnMoreUrl:
      'https://help.avochato.com/verify-your-business-with-a2p/register-for-a2p',
  },
  'trial-pending': {
    title: 'A2P Application Under Review',
    description:
      'Carriers review applications in 1–3 business days. Once approved, upgrade to a plan to start texting.',
    ctaLabel: 'Check Status',
    learnMoreUrl:
      'https://help.avochato.com/verify-your-business-with-a2p/register-for-a2p',
  },
  'trial-upgrade': {
    title: 'Upgrade to Start Texting',
    description:
      'Your A2P application is approved! Pick a plan to choose a number and start sending messages.',
    ctaLabel: 'Upgrade Now',
  },
  'paid-register': {
    title: 'Complete A2P Verification',
    description:
      'This inbox needs a registered brand and use case to send SMS to U.S. numbers.',
    ctaLabel: 'Register Now',
    learnMoreUrl:
      'https://help.avochato.com/verify-your-business-with-a2p/register-for-a2p',
  },
  'paid-choose-number': {
    title: 'Choose a Phone Number',
    description:
      'Your A2P registration is approved! Pick a phone number for this inbox to start texting.',
    ctaLabel: 'Choose Number',
  },
  'paid-sync': {
    title: 'Sync This Inbox',
    description:
      'Your A2P use case is approved. Sync a number to this inbox to start texting.',
    ctaLabel: 'Sync Now',
    learnMoreUrl:
      'https://help.avochato.com/verify-your-business-with-a2p/register-for-a2p',
  },
  'toll-free': {
    title: 'Verify Toll-Free Number',
    description:
      'Sending SMS text messages requires completing the Toll-Free Verification Process.',
    ctaLabel: 'Register Now',
    learnMoreUrl:
      'https://help.avochato.com/administrative/gKg5a8E76p4DLgaVdhAhDM/toll-free-number-verification/hyUfwemXBVddaxPTRd8Sg7',
  },
};

export const ReminderHeader = ({
  bannerStep,
  handleDisableBanner,
}: ReminderHeaderProps) => {
  const { subdomain, hasTcrBrand } = useContext(AccountInfoContext);
  const { navigate } = useNavigationActions();
  const config = BANNER_CONFIG[bannerStep];

  const handleCta = () => {
    if (handleDisableBanner) handleDisableBanner();
    switch (bannerStep) {
      case 'trial-register':
        navigate({ path: `/accounts/${subdomain}/tcr/first_submission`, params: {} });
        break;
      case 'trial-pending':
        navigate({ path: `/accounts/${subdomain}/tcr`, params: {} });
        break;
      case 'trial-upgrade':
        navigate({ path: `/accounts/${subdomain}/billing_v2/upgrade`, params: {} });
        break;
      case 'paid-register':
        navigate({ path: `/accounts/${subdomain}/${hasTcrBrand ? 'tcr/register_brand' : 'tcr/first_submission'}`, params: {} });
        break;
      case 'paid-choose-number':
        window.location.href = `/accounts/${subdomain}/number`;
        break;
      case 'paid-sync':
        navigate({ path: `/accounts/${subdomain}/tcr`, params: {} });
        break;
      case 'toll-free':
        navigate({ path: `/accounts/${subdomain}/numbers`, params: {} });
        break;
    }
  };

  const handleLearnMore = () => {
    if (config.learnMoreUrl) {
      window.open(config.learnMoreUrl, '_blank', 'noopener');
    }
  };

  const showLearnMore = bannerStep !== 'trial-upgrade' && config.learnMoreUrl;

  return (
    <BannerWrapper>
      <DotImage src={dotImage} alt="Avochato" />
      <Background src={bgImg} alt="Avochato" />
      <TextWrap>
        <Greeting>{config.title}</Greeting>
        <Description>{config.description}</Description>
      </TextWrap>
      <ButtonWrap>
        <RegisterButton type="button" onClick={handleCta}>
          {config.ctaLabel}
        </RegisterButton>
        {showLearnMore && (
          <LearnMoreButton type="button" onClick={handleLearnMore}>
            Learn More
          </LearnMoreButton>
        )}
        <XWrapper>
          <X
            onClick={handleDisableBanner}
            style={{ cursor: 'pointer', marginLeft: '10px', height: '20px', width: '20px' }}
            width="15px"
            height="15px"
          />
        </XWrapper>
      </ButtonWrap>
    </BannerWrapper>
  );
};

const BannerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #F5F3C8;
  height: 84px;
  width: 100%;
  position: relative;
  overflow: hidden;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  @media only screen and (max-width: 600px) {
    flex-direction: column;
    height: auto;
    padding: 16px 20px;
    gap: 8px;
  }
`;

const DotImage = styled.img`
  position: absolute;
  z-index: 1;
  @media only screen and (max-width: 600px) {
    display: none;
  }
`;

const Background = styled.img`
  position: absolute;
  z-index: 0;
  @media only screen and (max-width: 600px) {
    display: none;
  }
`;

const TextWrap = styled.text`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 120px;
  z-index: 2;
  @media only screen and (max-width: 600px) {
    margin-left: 0;
    text-align: center;
    align-items: center;
  }
`;

const Greeting = styled.p`
  font-size: 18px;
  font-weight: 800 !important;
  margin: 0 0 5px 0;
`;

const Description = styled.p`
  font-size: 12px;
  max-width: 650px;
  margin: 0;
  @media only screen and (max-width: 600px) {
    padding: 0 24px;
  }
`;
const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 20px;
  z-index: 2;
  @media only screen and (max-width: 600px) {
    padding: 0;
  }
`;

const XWrapper = styled.div`
  min-width: 20px;
  @media only screen and (max-width: 600px) {
    position: absolute;
    top: 8px;
    right: 8px;
  }
`;

const RegisterButton = styled.button`
  border: 1px solid #DD7019;
  font-family: Montserrat, sans-serif !important;
  cursor: pointer;
  padding: 8px 25px;
  font-size: 12px;
  text-align: center;
  background: #DD7019;
  color: white !important;
  border-radius: 30px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
  white-space: nowrap;
`;

const LearnMoreButton = styled.button`
  border: 1px solid #DD7019;
  font-family: Montserrat, sans-serif !important;
  cursor: pointer;
  padding: 8px 25px;
  font-size: 12px;
  text-align: center;
  color: #DD7019 !important;
  border-radius: 30px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  white-space: nowrap;
`;
